<template>
  <SettingsPageLayout>
    <DetailForm
      v-if="loaded"
      title="Notifications"
      description="Manage the notifications you receive for various events on Humaniz. By default, we have selected the notifications that we think are the most important to you, but feel free to make it your own."
      action="Save"
      hidecancel="true"
      @handle-action="updateUser"
    >
      <ToggleInput
        label="New Candidate Message"
        description="Triggered whenever a new message comes in for a candidate and it's been over 15 minutes without being read. Highly recommended as quick responses to candidates increase conversion rate significantly."
        :model-value="settings.notifyOnNewMessage"
        @update:model-value="settings.notifyOnNewMessage = $event"
      />
      <ToggleInput
        label="New Application Submitted"
        description="Triggered when a new application is created in Humaniz for your account. Highly recommended so you can quickly followup with a candidate that might have any questions as part of the application process."
        :model-value="settings.notifyOnNewApplication"
        @update:model-value="settings.notifyOnNewApplication = $event"
      />
      <ToggleInput
        label="New Candidate or Teammate Event Scheduled"
        description="Triggered whenever a Candidate or Teammate schedules a new event with you."
        :model-value="settings.notifyOnEventScheduled"
        @update:model-value="settings.notifyOnEventScheduled = $event"
      />
      <ToggleInput
        label="Scheduled Event Email Reminder"
        description="Triggered whenever a Candidate or Teammate has an upcoming scheduled event with you."
        :model-value="settings.notifyOnEventReminderEmail"
        @update:model-value="settings.notifyOnEventReminderEmail = $event"
      />
      <ToggleInput
        label="Scheduled Event SMS Reminder"
        description="Triggered whenever a Candidate or Teammate has an upcoming scheduled event with you."
        :model-value="settings.notifyOnEventReminderSMS"
        @update:model-value="settings.notifyOnEventReminderSMS = $event"
      />
      <ToggleInput
        label="Daily Recap Email"
        description="A daily email summarizing changes to your hiring pipeline over the last day."
        :model-value="settings.notifyDailyPipelineReport"
        @update:model-value="settings.notifyDailyPipelineReport = $event"
      />
      <ToggleInput
        label="Weekly Report Email"
        description="A weekly email highlighting recommended actions based on the previous week."
        :model-value="settings.notifyWeeklyDigestReport"
        @update:model-value="settings.notifyWeeklyDigestReport = $event"
      />
      <ToggleInput
        label="Stale Jobs Notification"
        description="A notification when you have jobs that haven't been updated in 4 weeks."
        :model-value="settings.notifyOnStaleJobs"
        @update:model-value="settings.notifyOnStaleJobs = $event"
      />
    </DetailForm>
  </SettingsPageLayout>
</template>

<script>
import SettingsPageLayout from '../../layouts/SettingsPageLayout/index.vue';
import DetailForm from '../../shared/DetailForm.vue';
import { useCurrentUserStore } from '@/stores/currentUser.ts';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import ToggleInput from '../../shared/ToggleInput.vue';
import { useUserCalendarStore } from '../../../stores/userCalendar.ts';
import { toast } from 'vue-sonner';

export default {
  components: {
    SettingsPageLayout,
    DetailForm,
    ToggleInput,
  },
  setup() {
    const userCalendarStore = useUserCalendarStore();
    const currentUserStore = useCurrentUserStore();
    const loadingSpinnerStore = useLoadingSpinnerStore();
    return { userCalendarStore, currentUserStore, loadingSpinnerStore };
  },
  data() {
    return {
      loaded: false,
      settings: {
        notifyOnNewMessage: false,
        notifyOnNewApplication: false,
        notifyDailyPipelineReport: false,
        notifyWeeklyDigestReport: false,
        notifyOnEventScheduled: false,
        notifyOnEventReminderEmail: false,
        notifyOnEventReminderSMS: false,
        notifyOnStaleJobs: false,
      },
    };
  },
  computed: {
    user() {
      return this.currentUserStore.currentUser;
    },
    currentUserNotificationSettings() {
      return this.userCalendarStore.currentUserNotificationSettings;
    },
  },
  async mounted() {
    await this.getUser();
  },
  methods: {
    async getUser() {
      await this.currentUserStore.loadCurrentUser();
      await this.userCalendarStore.loadUserCalendarData();
      this.settings = {
        notifyOnNewMessage: this.currentUserNotificationSettings.notifyOnNewMessage,
        notifyOnNewApplication: this.currentUserNotificationSettings.notifyOnNewApplication,
        notifyDailyPipelineReport: this.currentUserNotificationSettings.notifyDailyPipelineReport,
        notifyWeeklyDigestReport: this.currentUserNotificationSettings.notifyWeeklyDigestReport,
        notifyOnEventScheduled: this.currentUserNotificationSettings.notifyOnEventScheduled,
        notifyOnEventReminderEmail: this.currentUserNotificationSettings.notifyOnEventReminderEmail,
        notifyOnEventReminderSMS: this.currentUserNotificationSettings.notifyOnEventReminderSMS,
        notifyOnStaleJobs: this.currentUserNotificationSettings.notifyOnStaleJobs,
      };
      this.loaded = true;
    },
    async updateUser() {
      this.loadingSpinnerStore.showLocalLoadingSpinner();
      try {
        await this.userCalendarStore.updateUserNotifications(this.user, this.settings);
        this.loadingSpinnerStore.hideLocalLoadingSpinner();
        toast.success('Notifications updated', {
          action: { label: 'Dismiss' },
          description: 'Your Notifications were successfully updated.  Use the button below to dismiss this alert.',
        });
      } catch (error) {
        this.loadingSpinnerStore.hideLocalLoadingSpinner();
        toast.error('Update failed', {
          action: { label: 'Dismiss' },
          description: 'Failed to update notifications. Please try again.',
        });
      }
    },
  },
};
</script>
